import { useDispatch, useSelector } from "react-redux";

import CopyIconButton from "components/atoms/CopyIconButton";
import { GenericModal } from "components/GenericModal";

import { clearLastAddedLandingPage } from "../../../../redux/slices/landingPages/landingPages.slice";
import {
    EmptyUrlsState,
    UrlLabel,
    UrlList,
    UrlRow,
    UrlValueBox,
    UrlValueText,
} from "./URLsModal.styled";

const buildTrackingRows = (pageUrl: string) => [
    {
        key: "landing-page",
        title: "Landing Page URL",
        value: pageUrl || "",
    },
    {
        key: "facebook",
        title: "Facebook Tracking Parameters",
        value: "ad_id={{ad.id}}&adset_id={{adset.id}}&campaign_id={{campaign.id}}&ad_name={{ad.name}}&adset_name={{adset.name}}&campaign_name={{campaign.name}}&placement={{placement}}&site_source_name={{site_source_name}}",
    },
    {
        key: "tiktok",
        title: "TikTok Tracking Parameters",
        value: `${pageUrl || ""}?ad_id=__CID__&adset_id=__AID__&campaign_id=__CAMPAIGN_ID__&ad_name=__CID_NAME__&adset_name=__AID_NAME__&campaign_name=__CAMPAIGN_NAME__&placement=__PLACEMENT__&site_source_name=tt`,
    },
    {
        key: "snapchat",
        title: "Snapchat Tracking Parameters",
        value: `${pageUrl || ""}?ad_id={{ad.id}}&adset_id={{adSet.id}}&campaign_id={{campaign.id}}&ad_name={{creative.name}}&adset_name={{adSet.name}}&campaign_name={{campaign.name}}&site_source_name={{site_source_name}}`,
    },
    {
        key: "google-ads",
        title: "Google Ads Tracking Parameters",
        value: "{lpurl}?campaign_id={campaignid}&ad_name={creative}&adset_name={keyword}&campaign_name={_campaign}",
    },
];

const UrlRowItem = ({ title, value }: { title: string; value: string }) => (
    <UrlRow>
        <UrlLabel>{title}</UrlLabel>
        <UrlValueBox>
            <UrlValueText>{value}</UrlValueText>
            <CopyIconButton value={value} />
        </UrlValueBox>
    </UrlRow>
);

export interface LandingPageUrlsModalProps {
    open: boolean;
    onClose: () => void;
    landingPageName?: string;
    pageUrl?: string;
}

// Presentational — takes its data as props so it can be driven either by the
// redux "just created a landing page" flow (see `URLsModal` below) or by any
// other trigger (e.g. a manual preview button).
export const LandingPageUrlsModal = ({
    open,
    onClose,
    landingPageName,
    pageUrl,
}: LandingPageUrlsModalProps) => {
    const rows = buildTrackingRows(pageUrl || "");

    return (
        <GenericModal
            open={open}
            onClose={onClose}
            title="Landing Page Details"
            subtitle={landingPageName}
            maxWidth="sm"
            content={
                pageUrl ? (
                    <UrlList>
                        {rows.map((row) => (
                            <UrlRowItem
                                key={row.key}
                                title={row.title}
                                value={row.value}
                            />
                        ))}
                    </UrlList>
                ) : (
                    <EmptyUrlsState>No URL available.</EmptyUrlsState>
                )
            }
        />
    );
};

const URLsModal = () => {
    const lastAddedLandingPage = useSelector(
        (state: any) => state.landingPages.lastAddedLandingPage,
    );
    const lastAddedLandingPagePopulated = useSelector((state: any) =>
        state.landingPages.landingPages?.data?.find(
            (item: any) => item?.id === lastAddedLandingPage,
        ),
    );
    const dispatch = useDispatch();

    const handleOnHide = () => {
        dispatch(clearLastAddedLandingPage());
    };

    return (
        <LandingPageUrlsModal
            open={!!lastAddedLandingPagePopulated}
            onClose={handleOnHide}
            landingPageName={lastAddedLandingPagePopulated?.name}
            pageUrl={lastAddedLandingPagePopulated?.page_url}
        />
    );
};

export default URLsModal;
